// The infinite ground/grid SHADER: a shader material that draws cell lines
// (distance-faded) clipped to a rounded-rect footprint, plus the large floor
// plane that uses it. The same material is cloned per work-volume wall (see
// scene/work-volume-view) so every surface shares one grid look; uniforms
// (uGridHalfSize / uGridCenter / uGridOffset / uGridLines) are driven per
// surface there. Pure construction — main parents `ground` under gridGroup.

import * as THREE from "three";
import { CELL_SIZE } from "../model/voxel-grid";
import { GRID_LINE_COLOR } from "../view/voxel-mesh";

const MAJOR_CELL = 1.0;
const MINOR_CELL = CELL_SIZE;
const GROUND_SIZE = 600;

export const groundMat = new THREE.ShaderMaterial({
  uniforms: {
    uMajor: { value: MAJOR_CELL },
    uMinor: { value: MINOR_CELL },
    uMajorColor: { value: new THREE.Color(GRID_LINE_COLOR) },
    uMinorColor: { value: new THREE.Color(GRID_LINE_COLOR) },
    uBaseColor: { value: new THREE.Color(0x808080) },
    uBaseAlpha: { value: 1.0 },
    uMinorFadeStart: { value: 15.0 },
    uMinorFadeEnd: { value: 30.0 },
    uGridHalfSize: { value: new THREE.Vector2(1.5, 1.5) },
    uCornerRadius: { value: 0.0 },
    uFadeBand: { value: 0.001 },
    // Phase offset (plane-local) so cell lines stay aligned to the
    // absolute gridGroup cell grid regardless of where this plane is
    // centered — needed once walls can sit at off-cell positions.
    uGridOffset: { value: new THREE.Vector2(0, 0) },
    // Center (plane-local) of the rounded-rect footprint clip. Walls are
    // sized to their footprint (0); the floor offsets to the footprint.
    uGridCenter: { value: new THREE.Vector2(0, 0) },
    // Cell-line strength: 1 shows the grid, 0 leaves a clean surface.
    // Dropped to 0 in placement mode so furniture reads against bare walls.
    uGridLines: { value: 1.0 },
  },
  vertexShader: `
    varying vec2 vLocalXZ;
    varying vec3 vWorldPos;
    void main() {
      vLocalXZ = position.xy;
      vec4 wp = modelMatrix * vec4(position, 1.0);
      vWorldPos = wp.xyz;
      gl_Position = projectionMatrix * viewMatrix * wp;
    }
  `,
  fragmentShader: `
    precision highp float;
    varying vec2 vLocalXZ;
    varying vec3 vWorldPos;
    uniform float uMajor;
    uniform float uMinor;
    uniform vec3 uMajorColor;
    uniform vec3 uMinorColor;
    uniform vec3 uBaseColor;
    uniform float uBaseAlpha;
    uniform float uMinorFadeStart;
    uniform float uMinorFadeEnd;
    uniform vec2 uGridHalfSize;
    uniform float uCornerRadius;
    uniform float uFadeBand;
    uniform vec2 uGridOffset;
    uniform vec2 uGridCenter;
    uniform float uGridLines;

    float gridMask(vec2 p, float size) {
      vec2 coord = p / size;
      vec2 d = abs(fract(coord - 0.5) - 0.5) / fwidth(coord);
      float line = min(d.x, d.y);
      return 1.0 - clamp(line, 0.0, 1.0);
    }

    float roundedRectSDF(vec2 p, vec2 halfSize, float r) {
      vec2 q = abs(p) - halfSize + vec2(r);
      return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
    }

    void main() {
      vec2 p = vLocalXZ;
      // Lines align to the absolute cell grid (phase offset); footprint
      // clip is centered on uGridCenter.
      float minor = gridMask(p + uGridOffset, uMinor);
      float camDist = distance(cameraPosition, vWorldPos);
      float minorFade =
        1.0 - smoothstep(uMinorFadeStart, uMinorFadeEnd, camDist);
      float sdf = roundedRectSDF(p - uGridCenter, uGridHalfSize, uCornerRadius);
      float radiusFade = 1.0 - smoothstep(0.0, uFadeBand, sdf);

      // Only the minor (cell) lines contribute now — the major lines
      // would just ghost through at grazing angles where minor has
      // already faded, and they share uMinorColor so they add no
      // visual information at normal viewing.
      float lineMask = minor * minorFade * uGridLines;
      vec3 lineColor = uMinorColor;
      vec3 color = mix(uBaseColor, lineColor, lineMask);
      float alpha = mix(uBaseAlpha, 1.0, lineMask) * radiusFade;
      if (alpha < 0.005) discard;
      gl_FragColor = vec4(color, alpha);
    }
  `,
  transparent: true,
  side: THREE.DoubleSide,
  depthWrite: false,
});

export const ground = new THREE.Mesh(
  new THREE.PlaneGeometry(GROUND_SIZE, GROUND_SIZE),
  groundMat,
);
ground.rotation.x = -Math.PI / 2;
ground.renderOrder = -1;
